import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Title3,
  Body1,
  Spinner,
  Button,
  makeStyles,
  tokens,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Tooltip,
  Dropdown,
  Option,
  Input,
  Checkbox,
  ProgressBar,
} from '@fluentui/react-components';
import {
  DismissCircleRegular,
  DocumentTextRegular,
  SearchRegular,
  DismissRegular,
  DeleteDismissRegular,
  // Ícones para tipos de operação
  PersonAdd20Regular,
  PersonDelete20Regular,
  PersonEdit20Regular,
  PeopleTeam20Regular,
  Building20Regular,
  DocumentSync20Regular,
  // Ícones para erros e tempo
  ErrorCircle20Regular,
  Warning20Regular,
  Clock20Regular,
  // Ícone para tentativas
  ArrowRepeatAll20Regular,
  // Ícone para usuário
  Person20Regular,
} from '@fluentui/react-icons';
import { scheduledService } from '../../services/scheduledService';
import { snapshotService } from '../../services/snapshotService';
import type { ScheduledMessageDetails, SnapshotMessage, ScheduledOperationResponse } from '../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  tableCard: {
    padding: '20px',
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    overflowX: 'auto',
    maxHeight: '400px',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
  },
  filterDropdown: {
    minWidth: '180px',
  },
  searchInput: {
    minWidth: '280px',
    flexGrow: 1,
  },
  filterCount: {
    marginLeft: 'auto',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  bulkActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
  },
  correlationId: {
    fontFamily: 'monospace',
    fontSize: '11px',
    wordBreak: 'break-all',
  },
  compactCell: {
    fontSize: '12px',
  },
  scheduledTime: {
    fontWeight: 600,
    color: tokens.colorBrandForeground1,
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
    gap: '12px',
    alignItems: 'center',
  },
  paginationInfo: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  // Estilos para células com ícones
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  iconBlue: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  iconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  iconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  iconYellow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  iconGray: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
});

// Função para obter ícone baseado no tipo de operação
const getOperationIcon = (type: string) => {
  if (!type) return <DocumentSync20Regular />;
  const lowerType = type.toLowerCase();
  if (lowerType.includes('new') || lowerType.includes('create')) return <PersonAdd20Regular />;
  if (lowerType.includes('delete') || lowerType.includes('remove')) return <PersonDelete20Regular />;
  if (lowerType.includes('update') || lowerType.includes('edit')) return <PersonEdit20Regular />;
  if (lowerType.includes('section') || lowerType.includes('class') || lowerType.includes('roster') || lowerType.includes('enrollment')) return <PeopleTeam20Regular />;
  if (lowerType.includes('school')) return <Building20Regular />;
  return <DocumentSync20Regular />;
};

// Função para verificar se erro é recuperável
const isRecoverableError = (pattern: string): boolean => {
  if (!pattern) return false;
  const recoverable = ['Timeout', 'Rate Limited', 'Service Unavailable', 'Retry Limit Exceeded', 'Queue Disabled'];
  return recoverable.some(r => pattern.toLowerCase().includes(r.toLowerCase()));
};

// Função para verificar se é um tipo de Enrollment/Roster
const isEnrollmentOrRoster = (messageType: string): boolean => {
  if (!messageType) return false;
  const lowerType = messageType.toLowerCase();
  return lowerType.includes('enrollment') || lowerType.includes('roster');
};

// Função para obter o texto de exibição do usuário (Username para users, ExternalId para outros)
const getUserDisplay = (msg: SnapshotMessage): string => {
  const messageType = msg.messageType?.toLowerCase() || '';

  // Para tipos de usuário (Student, Teacher, Staff) - mostrar Username
  if (messageType.includes('student') || messageType.includes('teacher') || messageType.includes('staff')) {
    // Para Enrollment/Roster - mostrar ExternalId do usuário
    if (messageType.includes('enrollment') || messageType.includes('roster')) {
      return msg.externalId || 'N/A';
    }
    // Para New/Update/Delete de usuários - mostrar Username (que está em userPrincipalName)
    if (msg.userPrincipalName) {
      return msg.userPrincipalName;
    }
  }

  // Fallback para externalId
  return msg.externalId || 'N/A';
};

// Função para gerar tooltip com informações adicionais
const getUserTooltip = (msg: SnapshotMessage): string => {
  const parts: string[] = [];

  if (msg.externalId) {
    parts.push(`ID: ${msg.externalId}`);
  }

  if (isEnrollmentOrRoster(msg.messageType || '') && msg.sectionId) {
    parts.push(`Turma: ${msg.sectionId}`);
  }

  if (msg.displayName && msg.userPrincipalName) {
    // Se tiver ambos, mostra o displayName no tooltip quando o UPN está sendo exibido
    parts.push(`Nome: ${msg.displayName}`);
  }

  return parts.length > 0 ? parts.join(' | ') : '';
};

const PAGE_SIZE = 50;

export const ScheduledMessagesList: React.FC = () => {
  const styles = useStyles();
  const [messages, setMessages] = useState<SnapshotMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ScheduledMessageDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Estado de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Listas distintas do backend (para dropdowns)
  const [distinctMessageTypes, setDistinctMessageTypes] = useState<string[]>([]);
  const [distinctRetryCounts, setDistinctRetryCounts] = useState<number[]>([]);
  const [distinctErrorPatterns, setDistinctErrorPatterns] = useState<string[]>([]);

  // Estado dos filtros
  const [filters, setFilters] = useState({
    messageType: '' as string,
    searchText: '' as string,
    minRetryCount: null as number | null,
    errorPattern: '' as string,
  });

  // Estado de seleção
  const [selectedMessages, setSelectedMessages] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Estado para operação de cancelar tudo assíncrona
  const [cancelAllOperation, setCancelAllOperation] = useState<ScheduledOperationResponse | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Carregar mensagens quando filtros (exceto searchText) mudam
  useEffect(() => {
    loadMessages(1);
  }, [filters.messageType, filters.minRetryCount, filters.errorPattern]);

  // Debounce para searchText
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMessages(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchText]);

  const clearFilters = () => {
    setFilters({ messageType: '', searchText: '', minRetryCount: null, errorPattern: '' });
  };

  const hasActiveFilters = filters.messageType || filters.searchText || filters.minRetryCount !== null || filters.errorPattern;

  // Funções de seleção
  const toggleSelectAll = () => {
    if (selectedMessages.size === messages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(messages.map(m => m.sequenceNumber)));
    }
  };

  const toggleSelect = (sequenceNumber: number) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(sequenceNumber)) {
      newSelected.delete(sequenceNumber);
    } else {
      newSelected.add(sequenceNumber);
    }
    setSelectedMessages(newSelected);
  };

  const isAllSelected = messages.length > 0 && selectedMessages.size === messages.length;
  const hasSelection = selectedMessages.size > 0;

  // Ações em lote
  const handleBulkCancel = async () => {
    if (!confirm(`Tem certeza que deseja CANCELAR ${selectedMessages.size} mensagem(ns) agendada(s)?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      const result = await scheduledService.cancelBatch(Array.from(selectedMessages));
      alert(`${result.cancelled} mensagem(ns) cancelada(s) com sucesso!`);
      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error bulk cancelling:', err);
      alert(`Erro ao cancelar: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleCancelAll = async () => {
    if (!confirm(`Tem certeza que deseja CANCELAR TODAS as ${totalCount} mensagens agendadas?\n\nEsta operação pode demorar vários minutos.`)) {
      return;
    }

    try {
      setIsCancelling(true);
      setBulkLoading(true);

      // Inicia operação assíncrona
      const operation = await scheduledService.startCancelAllAsync();
      setCancelAllOperation(operation);

      // Polling com callback para atualizar progresso
      const finalOp = await scheduledService.pollOperation(
        operation.id,
        (op) => setCancelAllOperation(op),
        2000 // 2 segundos entre polls
      );

      if (finalOp.status === 'Completed') {
        alert(`Operação concluída!\n\n${finalOp.processed} mensagens canceladas\n${finalOp.failed} falhas\nDuração: ${finalOp.durationFormatted}`);
      } else if (finalOp.status === 'Failed') {
        alert(`Erro na operação: ${finalOp.errorMessage}`);
      } else if (finalOp.status === 'Cancelled') {
        alert('Operação cancelada pelo usuário.');
      }

      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error cancelling all:', err);
      alert(`Erro ao iniciar cancelamento: ${err.message}`);
    } finally {
      setIsCancelling(false);
      setCancelAllOperation(null);
      setBulkLoading(false);
    }
  };

  const handleCancelCancelAll = async () => {
    if (cancelAllOperation && confirm('Tem certeza que deseja cancelar a operação?')) {
      try {
        await scheduledService.cancelOperation(cancelAllOperation.id);
      } catch (err: any) {
        console.error('Error cancelling operation:', err);
      }
    }
  };

  const loadMessages = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const data = await snapshotService.getScheduledMessages(
        page,
        PAGE_SIZE,
        filters.messageType || undefined,
        filters.searchText || undefined,
        filters.minRetryCount ?? undefined,
        filters.errorPattern || undefined
      );

      setMessages(data.messages);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
      setTotalCount(data.totalCount);

      // Atualizar listas distintas (para os dropdowns)
      if (data.distinctMessageTypes) {
        setDistinctMessageTypes(data.distinctMessageTypes);
      }
      if (data.distinctRetryCounts) {
        setDistinctRetryCounts(data.distinctRetryCounts);
      }
      if (data.distinctErrorPatterns) {
        setDistinctErrorPatterns(data.distinctErrorPatterns);
      }

      // Limpar seleção ao mudar de página
      setSelectedMessages(new Set());
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar mensagens agendadas do snapshot');
      console.error('Error loading scheduled messages from snapshot:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.messageType, filters.searchText, filters.minRetryCount, filters.errorPattern]);

  const handleViewDetails = async (sequenceNumber: number) => {
    try {
      setDetailsLoading(true);
      const details = await scheduledService.getMessageDetails(sequenceNumber);
      setSelectedMessage(details);
    } catch (err: any) {
      console.error('Error loading message details:', err);
      alert(`Erro ao carregar detalhes: ${err.message}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCancel = async (sequenceNumber: number) => {
    if (!confirm('Tem certeza que deseja CANCELAR esta mensagem agendada?')) {
      return;
    }

    try {
      setActionLoading(sequenceNumber);
      await scheduledService.cancelMessage(sequenceNumber);
      alert('Mensagem cancelada com sucesso!');
      await loadMessages(currentPage);
    } catch (err: any) {
      console.error('Error cancelling message:', err);
      alert(`Erro ao cancelar: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatTimeUntil = (dateString: string) => {
    const scheduledTime = new Date(dateString);
    const now = new Date();
    const diffMs = scheduledTime.getTime() - now.getTime();

    if (diffMs < 0) return 'Agora';

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}min`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ${diffMins % 60}min`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ${diffHours % 24}h`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner label="Carregando mensagens agendadas..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Body1 className={styles.errorText}>Erro: {error}</Body1>
      </Card>
    );
  }

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title3>
          Mensagens Agendadas ({messages.length} de {totalCount})
        </Title3>
        <Button appearance="primary" onClick={() => loadMessages(1)}>
          Atualizar
        </Button>
      </div>

      {/* Barra de Filtros */}
      {(messages.length > 0 || hasActiveFilters) && (
        <div className={styles.filterBar}>
          <Dropdown
            className={styles.filterDropdown}
            placeholder="Tipo de Mensagem"
            selectedOptions={filters.messageType ? [filters.messageType] : []}
            onOptionSelect={(_, data) => setFilters({ ...filters, messageType: (data.optionValue as string) || '' })}
          >
            <Option value="">Todos os Tipos</Option>
            {distinctMessageTypes.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Dropdown>

          <Dropdown
            className={styles.filterDropdown}
            placeholder="Tentativas"
            selectedOptions={filters.minRetryCount !== null ? [String(filters.minRetryCount)] : []}
            onOptionSelect={(_, data) => setFilters({
              ...filters,
              minRetryCount: data.optionValue ? Number(data.optionValue) : null
            })}
          >
            <Option value="">Todas</Option>
            {distinctRetryCounts.filter(c => c > 0).map(count => (
              <Option key={count} value={String(count)} text={`≥ ${count}`}>≥ {count}</Option>
            ))}
          </Dropdown>

          <Dropdown
            className={styles.filterDropdown}
            placeholder="Tipo de Erro"
            selectedOptions={filters.errorPattern ? [filters.errorPattern] : []}
            onOptionSelect={(_, data) => setFilters({ ...filters, errorPattern: (data.optionValue as string) || '' })}
          >
            <Option value="">Todos os Erros</Option>
            {distinctErrorPatterns.map(pattern => (
              <Option key={pattern} value={pattern}>{pattern}</Option>
            ))}
          </Dropdown>

          <Input
            className={styles.searchInput}
            placeholder="Buscar por External ID, Correlation ID, UPN, Nome..."
            contentBefore={<SearchRegular />}
            value={filters.searchText}
            onChange={(_, data) => setFilters({ ...filters, searchText: data.value || '' })}
          />

          {hasActiveFilters && (
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={clearFilters}
            >
              Limpar
            </Button>
          )}

          <span className={styles.filterCount}>
            Página {currentPage} de {totalPages} ({totalCount} total)
          </span>
        </div>
      )}

      {/* Barra de Ações em Lote */}
      {messages.length > 0 && (
        <div className={styles.bulkActions}>
          <Button
            appearance="secondary"
            icon={<DismissCircleRegular />}
            disabled={!hasSelection || bulkLoading}
            onClick={handleBulkCancel}
          >
            Cancelar Selecionados ({selectedMessages.size})
          </Button>

          <div style={{ borderLeft: `1px solid ${tokens.colorNeutralStroke1}`, height: '24px', margin: '0 8px' }} />

          <Button
            appearance="subtle"
            icon={<DeleteDismissRegular />}
            disabled={bulkLoading || messages.length === 0}
            onClick={handleCancelAll}
          >
            Cancelar Tudo
          </Button>

          {bulkLoading && !isCancelling && <Spinner size="tiny" label="Processando..." />}
        </div>
      )}

      {/* Barra de Progresso do Cancel All */}
      {isCancelling && cancelAllOperation && (
        <Card style={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Body1 style={{ fontWeight: 600 }}>
                Cancelando mensagens agendadas... {cancelAllOperation.processed} de {cancelAllOperation.estimatedTotal}
              </Body1>
              <Button
                size="small"
                appearance="subtle"
                onClick={handleCancelCancelAll}
              >
                Cancelar
              </Button>
            </div>
            <ProgressBar value={cancelAllOperation.progressPercentage / 100} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
              <span>{cancelAllOperation.progressPercentage}% completo</span>
              <span>Duração: {cancelAllOperation.durationFormatted}</span>
              {cancelAllOperation.failed > 0 && (
                <span style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {cancelAllOperation.failed} falhas
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {messages.length === 0 && !loading ? (
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <Body1>Nenhuma mensagem agendada</Body1>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '40px' }}>
                  <Checkbox
                    checked={isAllSelected ? true : hasSelection ? 'mixed' : false}
                    onChange={toggleSelectAll}
                  />
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '200px' }}>Correlation ID</TableHeaderCell>
                <TableHeaderCell style={{ width: '140px' }}>Tipo</TableHeaderCell>
                <TableHeaderCell style={{ width: '180px' }}>UPN</TableHeaderCell>
                <TableHeaderCell style={{ width: '150px' }}>Data Inicial</TableHeaderCell>
                <TableHeaderCell style={{ width: '80px' }}>Em</TableHeaderCell>
                <TableHeaderCell style={{ width: '120px' }}>Último Erro</TableHeaderCell>
                <TableHeaderCell style={{ width: '90px' }}>Tentativas</TableHeaderCell>
                <TableHeaderCell style={{ width: '100px' }}>Ações</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((msg) => (
                <TableRow key={msg.sequenceNumber}>
                  <TableCell>
                    <Checkbox
                      checked={selectedMessages.has(msg.sequenceNumber)}
                      onChange={() => toggleSelect(msg.sequenceNumber)}
                    />
                  </TableCell>
                  <TableCell>
                    <span className={styles.correlationId}>
                      {msg.correlationId || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconGreen}`}>
                        {getOperationIcon(msg.messageType || '')}
                      </div>
                      <span>{msg.messageType || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getUserTooltip(msg) ? (
                      <Tooltip content={getUserTooltip(msg)} relationship="label">
                        <div className={styles.iconCell}>
                          <div className={`${styles.iconWrapper} ${styles.iconBlue}`}>
                            <Person20Regular />
                          </div>
                          <span style={{ fontSize: '12px' }}>{getUserDisplay(msg)}</span>
                        </div>
                      </Tooltip>
                    ) : (
                      <div className={styles.iconCell}>
                        <div className={`${styles.iconWrapper} ${styles.iconBlue}`}>
                          <Person20Regular />
                        </div>
                        <span style={{ fontSize: '12px' }}>{getUserDisplay(msg)}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={styles.scheduledTime}>
                      {msg.timestamp ? formatDate(msg.timestamp) : 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconBlue}`}>
                        <Clock20Regular />
                      </div>
                      <span>{msg.scheduledEnqueueTime ? formatTimeUntil(msg.scheduledEnqueueTime) : 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {msg.lastError ? (
                      <div className={styles.iconCell}>
                        <div className={`${styles.iconWrapper} ${isRecoverableError(msg.lastError) ? styles.iconYellow : styles.iconRed}`}>
                          {isRecoverableError(msg.lastError) ? <Warning20Regular /> : <ErrorCircle20Regular />}
                        </div>
                        <span>{msg.lastError}</span>
                      </div>
                    ) : (
                      <span className={styles.compactCell}>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconGray}`}>
                        <ArrowRepeatAll20Regular />
                      </div>
                      <span>{msg.retryCount}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Dialog>
                        <DialogTrigger disableButtonEnhancement>
                          <Tooltip content="Ver detalhes" relationship="label">
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<DocumentTextRegular />}
                              onClick={() => handleViewDetails(msg.sequenceNumber)}
                              disabled={actionLoading === msg.sequenceNumber}
                            />
                          </Tooltip>
                        </DialogTrigger>
                        <DialogSurface style={{ maxWidth: '800px' }}>
                          <DialogBody>
                            <DialogTitle>Detalhes da Mensagem Agendada</DialogTitle>
                            <DialogContent>
                              {detailsLoading ? (
                                <Spinner label="Carregando detalhes..." />
                              ) : selectedMessage ? (
                                <div className={styles.dialogContent}>
                                  <div>
                                    <strong>Message ID:</strong> {selectedMessage.messageId}
                                  </div>
                                  <div>
                                    <strong>Correlation ID:</strong> {selectedMessage.correlationId}
                                  </div>
                                  <div>
                                    <strong>Tipo:</strong> {selectedMessage.messageType}
                                  </div>
                                  <div>
                                    <strong>External ID:</strong> {selectedMessage.externalId}
                                  </div>
                                  <div>
                                    <strong>Agendado para:</strong>{' '}
                                    <span className={styles.scheduledTime}>
                                      {formatDate(selectedMessage.scheduledEnqueueTime)}
                                    </span>
                                  </div>
                                  <div>
                                    <strong>Enfileirado em:</strong> {formatDate(selectedMessage.enqueuedTime)}
                                  </div>
                                  <div>
                                    <strong>Retry Count:</strong> {selectedMessage.retryCount}
                                  </div>
                                  {Object.keys(selectedMessage.applicationProperties).length > 0 && (
                                    <div>
                                      <strong>Application Properties:</strong>
                                      <pre className={styles.codeBlock}>
                                        {JSON.stringify(selectedMessage.applicationProperties, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  <div>
                                    <strong>Message Body:</strong>
                                    <pre className={styles.codeBlock}>{selectedMessage.messageBody}</pre>
                                  </div>
                                </div>
                              ) : (
                                <Body1>Nenhum detalhe carregado</Body1>
                              )}
                            </DialogContent>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Fechar</Button>
                              </DialogTrigger>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>

                      <Tooltip content="Cancelar" relationship="label">
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<DismissCircleRegular />}
                          onClick={() => handleCancel(msg.sequenceNumber)}
                          disabled={actionLoading === msg.sequenceNumber}
                        />
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className={styles.loadMoreContainer}>
              <Button
                appearance="outline"
                disabled={currentPage === 1 || loading}
                onClick={() => loadMessages(currentPage - 1)}
              >
                Anterior
              </Button>

              <span className={styles.paginationInfo}>
                Página {currentPage} de {totalPages}
              </span>

              <Button
                appearance="outline"
                disabled={currentPage >= totalPages || loading}
                onClick={() => loadMessages(currentPage + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ScheduledMessagesList;
